<template>
  <div>
    <v-card block class="px-0 pt-0 mt-0 d-flex flex-row justify-space-around" color="primary" dark eager
            :key="$_sideMenu" large sticky-offset="$_offset" sticky-z-index="9"
            style="border-top: 1px solid white !important" tile
            v-sticky="!dialog">
      <div>
        <v-tooltip bottom v-if="isSaveDisabled && disabledSaveTooltip">
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" @click="handleSaveClick" style="display: inline-block; cursor: pointer;">
              <v-btn class="px-1 my-n1" @click.stop="save" :disabled="isSaveDisabled" text tile x-large>
                {{!id ? 'Create' : 'Save'}} Session
              </v-btn>
            </div>
          </template>
          <span>{{ disabledSaveTooltip }}</span>
        </v-tooltip>
        <div v-else @click="handleSaveClick" style="display: inline-block; cursor: pointer;">
          <v-btn class="px-1 my-n1" @click.stop="save" :disabled="isSaveDisabled" text tile x-large>
            {{!id ? 'Create' : 'Save'}} Session
          </v-btn>
        </div>
      </div>
    </v-card>
    <div class="pl-12 pt-4 ml-1 mb-n6 font-italic grey--text text--darken-1">*All fields except Notes are required</div>
    <v-col class="pb-0">
      <v-form class="px-10 mb-6" ref="form" v-model="valid">
        <div v-for="field in fields" :key="field.text">
          <div :class="['datetime-container', datetimeErrors[field.value].length ? 'error-state has-error' : '']"
              :style="field.value === 'login' ? 'margin: 20px 0 16px' : 'margin: 16px 0 16px'"
              v-if="DATETIME_FIELDS.includes(field.value)">
            <!-- Always render label; float when focused/has value/error, color red on error -->
            <label class="v-label theme--light"
                   :class="[
                     isDatetimeFloating(field.value) ? 'dt-label--float' : 'dt-label--base',
                     hasDatetimeError(field.value) ? 'error--text' : ''
                   ]"
                   :style="{ color: hasDatetimeError(field.value) ? '#B00020' : 'rgba(0,0,0,0.6)' }">
              {{ field.text }}
            </label>
            <datetime
              auto
              @blur="() => { dtFocused[field.value] = false; touchedFields[field.value] = true; validateDatetime(field.value); }"
              @focus="() => { dtFocused[field.value] = true; }"
              @input="() => { validateDatetime(field.value); validateDatetime('login'); validateDatetime('last_activity'); validateDatetime('logout'); }"
              :class="['theme-dominionBlue point line', datetimeErrors[field.value].length ? 'datetime-error' : '']"
              format="dd MMM yyyy hh:mm a"
              id="datetime-picker"
              :input-style="getDatetimeInputStyle(field.value)"
              input-class="dt-dark"
              :key="refresh + field.value"
              :placeholder="''"
              type="datetime"
              use12-hour
              v-model="session[field.value]"
              :week-start="7"/>
            <div class="v-messages theme--light">
              <div class="v-messages__wrapper">
                <div v-for="err in datetimeErrors[field.value]" :key="err" class="v-messages__message">{{ err }}</div>
              </div>
            </div>
          </div>
          <smart-input
            clearable
            dense
            :items="dropdownItems[field.list]"
            :key="refresh"
            :label="field.text"
            rows="4"
            :rules="getFieldRules(field.value)"
            :type="TEXT_INPUT_FIELDS.includes(field.value) ? 'VAutocomplete' : ['notes'].includes(field.value)
              ? 'VTextarea' : 'VTextField'"
            :style="field.value === 'username' ? 'margin: 34px 0 16px' : (field.value === 'type' ? 'margin: 22px 0 16px' : (field.value === 'login' ? 'margin: 22px 0 16px' : (field.value === 'notes' ? 'margin: 26px 0 16px' : 'margin: 18px 0 16px')))"
            :class="['notes'].includes(field.value) ? 'notes-textarea' : ''"
            @blur="TEXT_INPUT_FIELDS.includes(field.value) ? handleFieldInteraction(field.value) : null"
            @change="TEXT_INPUT_FIELDS.includes(field.value) ? handleFieldInteraction(field.value) : null"
            @input="TEXT_INPUT_FIELDS.includes(field.value) ? handleFieldInteraction(field.value) : null"
            v-else
            v-model="session[field.value]"/>
        </div>
      </v-form>
    </v-col>

    <snack :color="$_snack.color" :msg="$_snack.msg" v-model="$_snack.open" v-if="$_snack.cmpnt === $options.name"/>
    <ErrorDialog ref="errorDialog"/>
  </div>
</template>

<script>
import { SessionsAPIService } from '@/servicehandlers/SessionsAPIService';
import { UsersAPIService } from '@/servicehandlers/UsersAPIService';
import { Datetime } from 'vue-datetime';

const sessionsAPIService = new SessionsAPIService();
const usersAPIService = new UsersAPIService();

// 22 Nov 2025 JCH Constants for field arrays to reduce diff
const REQUIRED_FIELDS = ['username', 'type', 'login', 'last_activity', 'logout'];
const DATETIME_FIELDS = ['login', 'last_activity', 'logout'];
const TEXT_INPUT_FIELDS = ['username', 'type'];

export default {
  name: 'SessionEdit',
  components: { Datetime },
  props: {
    id: [String, Number],
    dialog: Boolean,
    default() {
      return {};
    }
  },
  data() {
    return {
      datetimeErrors: { login: [], last_activity: [], logout: [] },
      dtFocused: { login: false, last_activity: false, logout: false },
      dropdownItems: {
        types: ['Manual Logout', 'Automatic Logout', 'Vacation', 'Holiday', 'Floater', 'Sick Leave', 'Jury Duty',
                'Unpaid Leave', 'Penalty', 'Personal'] // 3 Oct 2025 JCH session types array
      },
      // 22 Nov 2025 JCH Constants for field arrays to reduce diff
      REQUIRED_FIELDS,
      DATETIME_FIELDS,
      TEXT_INPUT_FIELDS,
      
      fields: [
        {value: 'username',       text: 'Username', list: 'usernames'},
        {value: 'login',          text: 'Start'},
        {value: 'last_activity',  text: 'Last Activity'},
        {value: 'logout',         text: 'End'},
        {value: 'type',           text: 'Type', list: 'types' },
        {value: 'notes',   text: 'Notes' }, // 14 Nov 2025 JCH use 'notes' to match backend API
      ],
      refresh: false,
      session: {},
      touchedFields: { username: false, type: false, login: false, last_activity: false, logout: false }, // 17 Nov 2025 JCH Track which fields have been touched
      valid: false,
    };
  },

  created() {
  if (this.id) {
    sessionsAPIService.getSessionById(this.id, this.$router).then((data) => {
      data.username = data.user.username; // Flatten username for dropdown
      data.type = this.dropdownItems.types[data.session_note?.type] || ''; // 3 Oct 2025 JCH convert type num to text
      data.notes = data.session_note?.notes || ''; // 14 Nov 2025 JCH extract notes - use 'notes' to match backend API
      this.session = data; // 3 Oct 2025 JCH set reactive data
    }).catch((e) => {
      this.$refs.errorDialog.showError({ errorMessage: "Failed to get session", forDevelopers: e });
    });
  }

  usersAPIService.getActiveUsers(this.$router).then((users) => {
    this.dropdownItems['usernames'] = users.map(u => u.username).sort();
    this.refresh = !this.refresh;
  }).catch((e) => this.$refs.errorDialog.showError({ forDevelopers: e }));
},

  mounted() {},

  computed: {
    isSaveDisabled() {
      return !this.valid || !this.session.login || !this.session.last_activity || !this.session.logout;
    },
    disabledSaveTooltip() {// 17 Nov 2025 JCH Show required fields tooltip if save is disabled
      const m = [];
      if (!this.session.login) m.push('Start');
      if (!this.session.last_activity) m.push('Last Activity');
      if (!this.session.logout) m.push('End');
      // Show username/type if missing (will be marked as touched on save click)
      if (!this.session.username) m.push('Username');
      if (!this.session.type) m.push('Type');
      return m.length ? `Fill required: ${m.join(', ')}` : '';
    },
  },

  methods: {
    isDatetimeFloating(field) {
      // Match Username/Type behavior: only float when there's a value; stay on-line when empty + error (even if focused)
      return this.hasValidDate(field);
    },
    hasDatetimeError(field) {
      return !!(this.datetimeErrors && this.datetimeErrors[field] && this.datetimeErrors[field].length);
    },
    getDatetimeInputStyle(fieldValue) {
      const isError = this.datetimeErrors && this.datetimeErrors[fieldValue] && this.datetimeErrors[fieldValue].length > 0;
      return {
        width: '100%',
        margin: '0',
        padding: '2px 0 0 0',
        color: isError ? '#b00020' : 'rgba(0, 0, 0, 0.87)',
      };
    },
    // Treat any non-empty string as a "has value" state so labels float for formatted strings too.
    // Keep true Date objects validated via getTime().
    hasValidDate(fieldValue) {
      const value = this.session ? this.session[fieldValue] : null;
      if (!value) return false;
      if (value instanceof Date) return !isNaN(value.getTime());
      if (typeof value === 'string') return value.trim().length > 0;
      return false;
    },

    handleFieldInteraction(fieldValue) {// 17 Nov 2025 JCH Mark field as touched and validate on input/blur/change
      this.$set(this.touchedFields, fieldValue, true);
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.validate();
        }
      });
    },

    getFieldRules(fieldValue) {// 17 Nov 2025 JCH Return validation rules based on field type - rules re-evaluate when touchedFields changes
      if (fieldValue === 'notes') {
        return [(v) => !v || v.length <= 100 || 'Max 100 characters'];
      }
      if (TEXT_INPUT_FIELDS.includes(fieldValue)) {
        // Access touchedFields directly in rule for reactivity; show field-specific message
        const label = fieldValue === 'username' ? 'Username' : 'Type';
        return [(v) => !this.touchedFields[fieldValue] || !!v || `${label} is required`];
      }
      return [(v) => !!v || 'Field is required'];
    },

    handleSaveClick() {// 17 Nov 2025 JCH Mark all required fields as touched so errors show when save is clicked
      REQUIRED_FIELDS.forEach(f => { this.touchedFields[f] = true; });
      if (this.isSaveDisabled) {
        // Validate datetime fields
        DATETIME_FIELDS.forEach(f => this.validateDatetime(f));
        // Validate form to trigger smart-input errors (username, type)
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.validate();
          }
        });
      } else {
        this.save();
      }
    },

    save() {
      // 17 Nov 2025 JCH Mark all required fields as touched so errors show when saving
      REQUIRED_FIELDS.forEach(f => { this.touchedFields[f] = true; });
      DATETIME_FIELDS.forEach(f => this.validateDatetime(f));
      
      // Check if there are any validation errors
      const hasErrors = this.datetimeErrors.login.length > 0 || 
                        this.datetimeErrors.last_activity.length > 0 || 
                        this.datetimeErrors.logout.length > 0;
      if (hasErrors || !this.valid || !this.session.login || !this.session.last_activity || !this.session.logout) {
        return; // Don't save if validation fails
      }
      
      const sessionData = { ...this.session };// Copy to avoid mutating reactive object, e.g., type->number
      const baseKeys = ['user_id','login','logout','last_activity','type'];
      const allowedKeys = this.id 
        ? baseKeys.concat(['notes'])             // 14 Nov 2025 JCH PUT update: enable 'notes' field
        : ['username'].concat(baseKeys, ['notes']);// 14 Nov 2025 JCH POST create: enable 'notes' field

      Object.keys(sessionData).forEach(k => {// Remove nulls & disallowed keys to satisfy backend validation
        if (!allowedKeys.includes(k) || sessionData[k]==null) delete sessionData[k]});

      // Force numeric conversion
      if (sessionData.type && typeof sessionData.type === 'string') {
        const numericType = this.dropdownItems.types.indexOf(sessionData.type);
        sessionData.type = numericType;
      }

      const promise = this.id
        ? sessionsAPIService.updateSession(this.id, sessionData, this.$router) // Use original UUID string
        : sessionsAPIService.createSession(sessionData, this.$router);         // POST create

      return promise.then(() => {
        this.$store.dispatch('SET_SNACK',{ cmpnt:this.$options.name,
          msg:`Session has been ${this.id?'updated':'created'}`, open:true });
        
        if (window.opener) { // 15 Nov 2025 JCH Try multiple methods to refresh parent window if opened as popup
          if (typeof window.opener.refreshSessionsList === 'function') {
            window.opener.refreshSessionsList();
          } else if (window.opener.sessionsComponent && typeof window.opener.sessionsComponent.refreshSessions === 'function') {
            window.opener.sessionsComponent.refreshSessions();
          }
        }
      }).catch(e => {
        this.$refs.errorDialog.showError({ errorMessage:`Failed to ${this.id?'update':'create'} session.`,
          forDevelopers:e });
      });
    },

    validateDatetime(changedField) {
      this.datetimeErrors = { login: [], last_activity: [], logout: [] };
      const login = this.session.login ? new Date(this.session.login) : null;
      const last = this.session.last_activity ? new Date(this.session.last_activity) : null;
      const logout = this.session.logout ? new Date(this.session.logout) : null;

      // 17 Nov 2025 JCH Only show required errors if field has been touched and value is missing or invalid
      if (this.touchedFields.login && (!this.session.login || (login && isNaN(login.getTime())))) this.datetimeErrors.login.push("Start is required");
      if (this.touchedFields.last_activity && (!this.session.last_activity || (last && isNaN(last.getTime())))) this.datetimeErrors.last_activity.push("Last Activity is required");
      if (this.touchedFields.logout && (!this.session.logout || (logout && isNaN(logout.getTime())))) this.datetimeErrors.logout.push("End is required");

      // Date logic validation
      if (login && !isNaN(login.getTime()) && logout && !isNaN(logout.getTime()) && login > logout && this.datetimeErrors.login.length === 0) {
        this.datetimeErrors.login.push("Start must not be after End");
      }
      if (login && !isNaN(login.getTime()) && logout && !isNaN(logout.getTime()) && logout < login && this.datetimeErrors.logout.length === 0) {
        this.datetimeErrors.logout.push("End must be after Start");
      }
      if (login && !isNaN(login.getTime()) && last && !isNaN(last.getTime()) && last < login && this.datetimeErrors.last_activity.length === 0) {
        this.datetimeErrors.last_activity.push("Last Activity must not be before Start");
      }
      if (logout && !isNaN(logout.getTime()) && last && !isNaN(last.getTime()) && last > logout && this.datetimeErrors.last_activity.length === 0) {
        this.datetimeErrors.last_activity.push("Last Activity must not be after End");
      }
    },
  }
};
</script>

<style>
/* Style datetime to match smart-input which uses Vuetify input components */
.datetime-container {
  position: relative;
  margin: 0; /* container margins controlled inline for consistent spacing */
  min-height: 56px; /* match Vuetify v-text-field height; expands if messages appear */
  padding-top: 2px; /* slight headroom for visual parity */
}

.datetime-container label.v-label {
  position: absolute;
  left: 0;
  top: 11px; /* Match Username/Type label spacing from input line */
  margin: 0 !important;
  font-size: 1rem; /* base label size */
  color: rgba(0, 0, 0, 0.6);
  font-weight: 400;
  line-height: 1;
  opacity: 1 !important;
  pointer-events: none; /* prevent click overlap */
  z-index: 5; /* ensure label sits above input text */
  transform: none;
  transform-origin: left top;
}

.datetime-container.error-state label.v-label {
  color: #b00020 !important;
  display: block !important;
  /* Match Username/Type: stay on-line when empty + error (only float when has value) */
  /* transform will be controlled by dt-label--float/dt-label--base classes */
  z-index: 6 !important; /* ensure visible over input and underline */
}

.datetime-container .v-label.dt-label--float {
  /* Lift ~3px higher to align with v-text-field floating label */
  transform: scale(0.75) translateY(-29px);
}
.datetime-container .v-label.dt-label--base {
  transform: none;
}
/* Hide labels for datetime fields; rely on placeholder instead for consistency with error state */
/* do not hide labels; show like Vuetify text fields */

.datetime-container .line {
  padding-bottom: 0.3px;
  margin-top: 2px; /* 1px tighter above underline */
  border-bottom: 1px solid rgba(0, 0, 0, 0.42);
  width: 100%;
}

.datetime-container.error-state .line {
  border-bottom: 1px solid #b00020 !important;
}

.datetime-container .datetime-field.line:before,
.datetime-container .datetime-field.line:after {
  display: none !important;
  content: none !important;
  height: 0 !important;
  border: none !important;
}

.datetime-container.error-state input {
  box-shadow: none !important;
}

.datetime-container input {
  border: none;
  outline: none;
  width: 100%;
  font-size: 16px !important;
  font-weight: 400 !important; /* Match Vuetify default font-weight */
  background-color: transparent;
  box-shadow: none;
  color: rgba(0, 0, 0, 0.87) !important; /* Match Vuetify text field default color */
}
/* 20 Nov 2025 JCH Override vue-datetime placeholder to match Vuetify - consolidated selectors */
.datetime-container input::placeholder,
.datetime-container input::-webkit-input-placeholder,
.datetime-container input::-moz-placeholder,
.datetime-container input:-ms-input-placeholder,
.vdatetime-input::placeholder,
.vdatetime-input::-webkit-input-placeholder,
.vdatetime-input::-moz-placeholder,
.vdatetime-input:-ms-input-placeholder {
  color: rgba(0, 0, 0, 0.87) !important;
  -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
  opacity: 1 !important;
}

/* Error state placeholder color */
.datetime-container.error-state input::placeholder,
.datetime-container.error-state input::-webkit-input-placeholder,
.datetime-container.error-state input::-moz-placeholder,
.datetime-container.error-state input:-ms-input-placeholder {
  color: #b00020 !important;
  -webkit-text-fill-color: #b00020 !important;
}

/* Input text color - default and error states */
.datetime-container .vdatetime-input {
  color: rgba(0, 0, 0, 0.87) !important;
}
.datetime-container.error-state .vdatetime-input,
.datetime-container .datetime-error .vdatetime-input {
  color: #b00020 !important;
}

/* Error state label styling - override base label color with high specificity */
.datetime-container.error-state label.v-label.theme--light.error--text,
.datetime-container.has-error label.v-label.theme--light.error--text,
.datetime-container.error-state label.v-label.error--text,
.datetime-container.has-error label.v-label.error--text,
.datetime-container.error-state label.v-label,
.datetime-container.has-error label.v-label,
.datetime-container label.v-label.error--text,
.datetime-container.error-state .v-label.theme--light,
.datetime-container.has-error .v-label.theme--light {
  color: #b00020 !important;
  opacity: 1 !important;
}

.datetime-container .v-messages__message {
  color: #b00020 !important;
  font-size: 0.75rem; /* match SmartInput/Vuetify default */
  line-height: 1.3;
  margin-top: 6px !important; /* match v-text-field spacing below underline */
}

.datetime-container.has-error label.v-label {
  margin-bottom: 1px !important;
}

.datetime-container.has-error .v-messages__message {
  margin-top: 2px !important;
  font-size: 0.75rem; /* keep size consistent with SmartInput errors */
  line-height: 1.2;
}

/* 17 Nov 2025 JCH Move resize handle to left, align clearable icon at right */
.notes-textarea .v-input {
  position: relative;
  direction: ltr !important;
}
.notes-textarea .v-input__control {
  direction: rtl; /* Move resize handle to left */
}
.notes-textarea .v-textarea textarea {
  direction: ltr;
  text-align: left;
  padding-right: 28px !important; /* Prevent text from overlapping X icon */
}
/* Clearable icon and append slots - align at right edge */
.notes-textarea [class*="icon--clear"],
.notes-textarea [class*="icon_clear"],
.notes-textarea [class*="append"] {
  position: absolute !important;
  right: 0px !important;
  left: auto !important;
  top: 4px !important;
  z-index: 10 !important;
  transform: none !important;
  direction: ltr !important;
}

.dt-dark {
  color: rgba(0, 0, 0, 0.87) !important;
}
.datetime-container.error-state .dt-dark {
  color: #b00020 !important;
}
/* dt-dark class placeholder overrides (scoped via input-class="dt-dark") */
.dt-dark::placeholder,
.dt-dark::-webkit-input-placeholder,
.dt-dark::-moz-placeholder,
.dt-dark:-ms-input-placeholder {
  color: rgba(0, 0, 0, 0.6) !important;
  -webkit-text-fill-color: rgba(0, 0, 0, 0.6) !important;
  opacity: 1 !important;
}
.datetime-container.error-state .dt-dark::placeholder,
.datetime-container.error-state .dt-dark::-webkit-input-placeholder,
.datetime-container.error-state .dt-dark::-moz-placeholder,
.datetime-container.error-state .dt-dark:-ms-input-placeholder {
  color: #b00020 !important;
  -webkit-text-fill-color: #b00020 !important;
}

/* 20 Nov 2025 JCH removed mobile overlay tweak; Sessions.vue will handle datepicker z-index */

</style>
